// ============================================
// 📡 API.JS - Todas as funções de chamada HTTP
// ============================================
// ✅ MULTI-TENANT: todas as chamadas incluem tenant_id
// ============================================

import { WEBHOOKS, IMGBB_API_KEY, MODELO_CSV } from '../config/constants';
// MULTI-TENANT: helpers de isolamento por tenant
import supabase, { withTenant, injectTenantId, getCurrentTenantId, cabecalhosComLogin } from './supabaseClient';
import { processarCsvVendas } from './importacaoVendas';

// ============================================
// 🏢 CRM - CLIENTES
// ============================================

// Tenta carregar clientes via RPC (retorna dados enriquecidos com última compra etc.)
// Suporta dois formatos: array direto OU objeto { clientes, total }
const tentarCarregarViaRpc = async (tenantId) => {
  try {
    const { data, error } = await supabase.rpc('buscar_clientes_crm', {
      p_tenant_id: tenantId || null
    });
    if (!error) {
      const clientes = Array.isArray(data) ? data : (data?.clientes || []);
      if (clientes.length > 0) {
        console.log('✅ [API] Clientes carregados via RPC:', clientes.length);
        return clientes;
      }
    } else {
      console.warn('⚠️ [API] RPC falhou, tentando fallback direto:', error.message);
    }
  } catch (err) {
    console.warn('⚠️ [API] RPC falhou com exceção:', err);
  }
  return null;
};

// Fallback: busca direta na tabela clientes
// OTIMIZAÇÃO: adicionado limit(1000) para evitar egress excessivo
const tentarCarregarViaFallback = async (tenantId) => {
  try {
    let query = supabase.from('clientes').select('*').order('nome').limit(1000);
    if (tenantId) query = query.eq('tenant_id', tenantId);
    const { data: fallback, error: errFb } = await query;
    if (!errFb && fallback) {
      console.log('✅ [API] Clientes carregados via fallback:', fallback.length);
      return fallback;
    }
  } catch (errFb) {
    console.error('❌ [API] Fallback também falhou:', errFb);
  }
  return null;
};

/**
 * Carrega lista de clientes do CRM
 * Original: linhas 744-761
 */
export const carregarClientes = async () => {
  console.log('🔍 [API] Carregando clientes do Supabase...');
  const tenantId = getCurrentTenantId();

  const viaRpc = await tentarCarregarViaRpc(tenantId);
  if (viaRpc) return viaRpc;

  const viaFallback = await tentarCarregarViaFallback(tenantId);
  if (viaFallback) return viaFallback;

  return [];
};

// Formato que as telas esperam (herdado da resposta do n8n)
const paraObservacaoCrm = (linha) => ({
  id: linha.id,
  texto: linha.observacao,
  data: linha.created_at
});

/**
 * Carrega observações de um cliente específico
 * Migrado de webhook n8n para Supabase direto em 2026-09-26 (RLS isola o tenant).
 */
export const buscarObservacoes = async (telefone) => {
  const { data, error } = await withTenant(
    supabase.from('Observacoes')
      .select('id, observacao, created_at')
      .eq('telefone', telefone)
  ).order('created_at', { ascending: false });
  if (error) {
    console.error('❌ [API] Erro ao buscar observações:', error.message);
    return [];
  }
  return (data || []).map(paraObservacaoCrm);
};

/**
 * Salva uma nova observação para um cliente
 * Migrado de webhook n8n para Supabase direto em 2026-09-26.
 */
export const salvarObservacao = async (telefone, nome, observacao) => {
  const { data, error } = await supabase
    .from('Observacoes')
    .insert([injectTenantId({ telefone, nome, observacao })])
    .select('id, observacao, created_at')
    .single();
  if (error) throw error;
  return { sucesso: true, observacao: paraObservacaoCrm(data) };
};

/**
 * Exclui uma observação
 * Migrado de webhook n8n para Supabase direto em 2026-09-26.
 */
export const excluirObservacao = async (id) => {
  const { error } = await withTenant(
    supabase.from('Observacoes').delete().eq('id', id)
  );
  if (error) throw error;
  return { sucesso: true, id };
};

/**
 * Atualiza status de bloqueio do cliente diretamente no Supabase.
 * bloqueado=true  → active=true  (cliente "processado", não receberá disparos)
 * bloqueado=false → active=false (cliente liberado para receber disparos)
 * Migrado de webhook n8n para Supabase direto (CORS bloqueava a chamada).
 */
export const atualizarBloqueio = async (telefone, bloqueado) => {
  const { error } = await withTenant(
    supabase.from('clientes')
      .update({ active: bloqueado })
      .eq('telefone', telefone)
  );
  if (error) throw error;
  return { success: true };
};

/**
 * Atualiza bloqueio permanente do cliente diretamente no Supabase.
 * Clientes com bloqueado_permanente=true NÃO são resetados pelo "Resetar Status".
 */
export const atualizarBloqueadoPermanente = async (telefone, bloqueadoPermanente) => {
  // MULTI-TENANT: garante que só atualiza cliente do tenant correto
  const { error } = await withTenant(
    supabase.from('clientes')
      .update({ bloqueado_permanente: bloqueadoPermanente })
      .eq('telefone', telefone)
  );
  if (error) throw error;
  return { success: true };
};

/**
 * Após um Resetar Status, re-aplica o bloqueio para clientes permanentemente bloqueados.
 * Garante que eles não voltem a receber disparos mesmo após o reset.
 */
export const reAplicarBloqueiosPermanentes = async () => {
  // Um UPDATE só, no Supabase (mesma regra de atualizarBloqueio: bloqueado → active=true).
  // Antes chamava o webhook atualizar-bloqueio do n8n, um por cliente, e esse fluxo não
  // existe mais: o 404 era engolido e o "Resetar status" liberava para disparo quem tinha
  // pedido para nunca receber mensagem (corrigido em 2026-09-26).
  if (!getCurrentTenantId()) throw new Error('Empresa não identificada. Recarregue a página.');
  const { count, error } = await withTenant(
    supabase.from('clientes').update({ active: true }, { count: 'exact' }).eq('bloqueado_permanente', true)
  );
  if (error) throw error;
  return { totalBloqueados: count || 0 };
};

/**
 * Gera resumo IA das conversas de um cliente
 * Migrado de webhook n8n para /api/resumo-conversa em 2026-09-26. O token do
 * usuário vai junto: a função consulta o Supabase com ele (RLS) antes da OpenAI.
 */
export const gerarResumoIA = async (telefone) => {
  const response = await fetch('/api/resumo-conversa', {
    method: 'POST',
    headers: await cabecalhosComLogin(),
    body: JSON.stringify({ telefone, tenant_id: getCurrentTenantId() })
  });
  return await response.json();
};

/**
 * Busca status de IA no Supabase
 * Original: linhas 949-977
 * MIGRADO → Supabase direto em 2026-02-28
 * Tabela: Controle_Atendimento (SELECT WHERE telefone IN (...))
 * Padrão para telefones não encontrados: { iaAtiva: true, emAtendimento: false, tags: [] }
 */
export const buscarStatusSupabase = async (telefones) => {
  const telefonesStr = (telefones || []).map(t => String(t));
  const { data, error } = await withTenant(
    supabase.from('Controle_Atendimento')
      .select('telefone, ia_ativa, em_atendimento, tags')
      .in('telefone', telefonesStr)
  );
  if (error) throw error;
  const resultados = {};
  (data || []).forEach(registro => {
    resultados[registro.telefone] = {
      iaAtiva: registro.ia_ativa === true,
      emAtendimento: registro.em_atendimento === true,
      tags: registro.tags || []
    };
  });
  telefonesStr.forEach(tel => {
    if (!resultados[tel]) {
      resultados[tel] = { iaAtiva: true, emAtendimento: false, tags: [] };
    }
  });
  return { sucesso: true, status: resultados };
};

// ============================================
// 💬 ATENDIMENTO IA
// ============================================

/**
 * Lista instâncias ativas do WhatsApp
 * MULTI-TENANT: GET com tenant_id como query param
 * n8n lê via $json.query.tenant_id
 */
export const listarInstancias = async () => {
  const tenantId = getCurrentTenantId();
  try {
    const response = await fetch(
      `${WEBHOOKS.LISTAR_INSTANCIAS}?tenant_id=${tenantId}`,
      { method: 'GET' }
    );
    if (!response.ok) return { success: false, instancias: [] };
    const data = await response.json();
    return { success: true, instancias: data.instancias || [] };
  } catch (error) {
    return { success: false, instancias: [] };
  }
};

// enviarMensagem e controlarIA saíram em 2026-09-26: ninguém as importava e
// apontavam para webhooks públicos do n8n. O envio vive em /api/enviar-mensagem
// e o liga/desliga da IA em useConversas.toggleControleIA.

// ============================================
// 🚀 DISPAROS
// ============================================
// MIGRADO → /api/disparos em 2026-09-26 (src/services/disparosApi.js). Os fluxos
// SALVAR CONFIG DISPARO (GRUPO/PERSO) e DISPARADOR MASTER/PERSO do n8n, a fila,
// a sessão (disparos_sessao) e o agendamento no navegador saíram daqui.

// ============================================
// 🖼️ UPLOAD DE IMAGENS
// ============================================

/**
 * Upload de imagem para ImgBB
 * Original: linhas 1794-1815
 */
export const uploadParaImgBB = async (file) => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('key', IMGBB_API_KEY);

  const response = await fetch('https://api.imgbb.com/1/upload', {
    method: 'POST',
    body: formData
  });

  const data = await response.json();

  if (data.success) {
    return {
      link: data.data.url,
      deleteUrl: data.data.delete_url,
      thumb: data.data.thumb?.url || data.data.url
    };
  }
  throw new Error(data.error?.message || 'Erro no upload');
};

// ============================================
// ⚙️ CONFIGURAÇÃO IA
// ============================================

/**
 * Carrega configuração da IA
 * Original: linhas 1369-1384
 * MIGRADO → Supabase direto em 2026-02-28
 * Tabela: Configuracao_IA (SELECT * WHERE ativo=true ORDER BY created_at DESC LIMIT 1)
 */
export const carregarConfigIA = async () => {
  const { data, error } = await withTenant(
    supabase.from('Configuracao_IA').select('*').eq('ativo', true).order('created_at', { ascending: false }).limit(1)
  );
  if (error) throw error;
  return data?.[0] || null;
};

// ============================================
// 📊 RELATÓRIOS — chamada direta ao Supabase (sem n8n)
// ============================================

const warnRpcError = (nome, { error }) => {
  if (error) console.warn(`${nome}:`, error.message);
};

/**
 * Carrega dados de relatórios via Supabase RPC direto (sem n8n).
 * Executa os 5 RPCs em paralelo → muito mais rápido.
 * Historico_Conversas TEM tenant_id → passamos p_tenant_id para filtrar por empresa.
 * ATENÇÃO: requer rodar supabase/migrations/003_otimizar_relatorios.sql no Supabase.
 */
export const carregarDadosRelatorios = async (periodo = 7) => {
  const tenantId = getCurrentTenantId();
  try {
    const [metricas, volumeDiario, porHorario, tags, topClientes] = await Promise.all([
      supabase.rpc('relatorio_metricas_gerais', { periodo_dias: periodo, p_tenant_id: tenantId }),
      supabase.rpc('relatorio_volume_diario',   { periodo_dias: periodo, p_tenant_id: tenantId }),
      supabase.rpc('relatorio_conversas_por_horario', { periodo_dias: periodo, p_tenant_id: tenantId }),
      supabase.rpc('relatorio_tags_mais_usadas', { limite: 10, p_tenant_id: tenantId }),
      supabase.rpc('relatorio_top_clientes',    { periodo_dias: 30, limite: 10, p_tenant_id: tenantId })
    ]);

    warnRpcError('relatorio_metricas_gerais', metricas);
    warnRpcError('relatorio_volume_diario', volumeDiario);
    warnRpcError('relatorio_conversas_por_horario', porHorario);
    warnRpcError('relatorio_tags_mais_usadas', tags);
    warnRpcError('relatorio_top_clientes', topClientes);

    return {
      sucesso: true,
      periodo,
      metricas: metricas.data?.[0] || {},
      volumeDiario: volumeDiario.data || [],
      porHorario: porHorario.data || [],
      tags: tags.data || [],
      topClientes: topClientes.data || []
    };
  } catch (err) {
    console.error('Erro ao carregar relatórios:', err);
    return null;
  }
};

/**
 * Carrega dados de disparos para relatórios — Supabase direto (sem n8n)
 * Tabela: disparos_log (tem tenant_id + RLS)
 * Retorna { success, relatorio: [...] } para o RelatoriosTab fazer o agrupamento
 */
export const carregarDadosDisparos = async () => {
  try {
    // OTIMIZAÇÃO: reduzido de 5000 para 1000 — suficiente para gráficos de relatório
    const { data, error } = await withTenant(
      supabase
        .from('disparos_log')
        .select('tipo_mensagem, status')
        .order('enviado_em', { ascending: false })
        .limit(1000)
    );
    if (error) throw error;
    return { success: true, relatorio: data || [] };
  } catch (err) {
    console.warn('carregarDadosDisparos:', err.message);
    return { success: false, relatorio: [] };
  }
};

/**
 * Carrega estatísticas de disparos de um cliente
 * Migrado de webhook n8n para Supabase direto em 2026-09-26. Filtra o telefone
 * no banco: o n8n trazia o disparos_log inteiro do tenant (o PostgREST corta em 1000).
 */
export const carregarEstatisticasCliente = async (telefone) => {
  const ultimos8 = telefone.slice(-8);
  const { data, error } = await withTenant(
    supabase.from('disparos_log').select('*').like('telefone', `%${ultimos8}`)
  );
  if (error) {
    console.error('❌ [API] Erro ao carregar estatísticas do cliente:', error.message);
    return null;
  }
  const doCliente = data || [];
  return {
    total: doCliente.length,
    entregues: doCliente.filter(i => ['entregue', 'lido', 'respondido'].includes(i.status)).length,
    lidas: doCliente.filter(i => ['lido', 'respondido'].includes(i.status)),
    respondidas: doCliente.filter(i => i.status === 'respondido')
  };
};

// ============================================
// 📁 CONFIGURAÇÕES - UPLOAD DE CLIENTES
// ============================================

/**
 * Carrega estatísticas de clientes
 * Migrado de webhook n8n para a RPC stats_clientes_crm em 2026-09-26 (migration 069).
 * A tela espera { success, stats }.
 */
export const carregarStatsClientes = async () => {
  const { data, error } = await supabase.rpc('stats_clientes_crm', {
    p_tenant_id: getCurrentTenantId()
  });
  if (error) {
    console.error('❌ [API] Erro ao carregar estatísticas:', error.message);
    return { success: false, stats: null };
  }
  return { success: true, stats: data };
};

const LOTE_IMPORTACAO = 500;

const mensagemImportacao = (total, erros) => (
  erros > 0
    ? `${total} registros importados! (${erros} linhas ignoradas por data, telefone ou nome inválidos)`
    : `${total} registros importados!`
);

/**
 * Upload de arquivo de clientes (CSV/Excel já convertido em CSV pela tela)
 * Migrado de webhook n8n para Supabase direto em 2026-09-26. Insere em vendas;
 * o trigger criar_cliente_automatico cria o cliente.
 */
export const uploadClientes = async (csvTexto) => {
  if (String(csvTexto).startsWith('PDF_BASE64:')) {
    return { success: false, message: 'Importação de PDF não é suportada. Use CSV ou Excel.' };
  }
  const { registros, erros } = processarCsvVendas(csvTexto);
  if (registros.length === 0) {
    return { success: false, message: 'Nenhum registro encontrado', total_importado: 0 };
  }
  for (let i = 0; i < registros.length; i += LOTE_IMPORTACAO) {
    const lote = injectTenantId(registros.slice(i, i + LOTE_IMPORTACAO));
    const { error } = await supabase.from('vendas').insert(lote);
    if (error) {
      return { success: false, message: `Erro após importar ${i} registros: ${error.message}`, total_importado: i };
    }
  }
  const total = registros.length;
  return { success: true, message: mensagemImportacao(total, erros), total, total_importado: total };
};

/**
 * Limpa base de clientes (vendas e clientes do tenant)
 * Migrado de webhook n8n para Supabase direto em 2026-09-26.
 */
// Sem tenant, withTenant não filtra e um super_admin apagaria/alteraria todos os tenants.
const SEM_TENANT = { success: false, message: 'Empresa não identificada. Recarregue a página e tente de novo.' };

export const limparBaseClientes = async () => {
  if (!getCurrentTenantId()) return SEM_TENANT;
  const { error: errVendas } = await withTenant(supabase.from('vendas').delete().gt('id', 0));
  if (errVendas) return { success: false, message: errVendas.message };
  const { error: errClientes } = await withTenant(supabase.from('clientes').delete().gt('id', 0));
  if (errClientes) return { success: false, message: errClientes.message };
  return { success: true, message: 'Base limpa com sucesso!' };
};

/**
 * Reseta status active de todos os clientes do tenant
 * Migrado de webhook n8n para Supabase direto em 2026-09-26.
 */
export const resetarActiveClientes = async () => {
  if (!getCurrentTenantId()) return SEM_TENANT;
  const { error } = await withTenant(
    supabase.from('clientes').update({ active: false }).eq('active', true)
  );
  if (error) return { success: false, message: error.message };
  return { success: true, message: 'Status resetado para todos os clientes!' };
};

/**
 * Baixa modelo CSV
 * Original: linhas 731-742
 */
export const baixarModeloCSV = () => {
  const blob = new Blob([MODELO_CSV], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'modelo_clientes.csv';
  link.click();
};

// ============================================
// 📱 WHATSAPP
// ============================================

/**
 * Cria nova instância WhatsApp
 * Original: linhas 5717-5726
 */
export const criarInstanciaWhatsApp = async (tenantSubdominio = '') => {
  const response = await fetch(WEBHOOKS.CRIAR_INSTANCIA, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_subdominio: tenantSubdominio })
  });
  return await response.json();
};

/**
 * Gera QR Code para instância
 * Original: linhas 5737-5746
 */
export const gerarQRCode = async (instanceName) => {
  const tenantId = getCurrentTenantId();
  const response = await fetch(WEBHOOKS.GERAR_QR, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ instanceName, tenant_id: tenantId })
  });
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

// ============================================
// 📍 MAPEAMENTO DE LOJAS
// ============================================

/**
 * Salvar mapeamento de instância → loja
 */
export const salvarMapeamentoLoja = async (instanceName, lojaNome) => {
  const { data, error } = await supabase
    .from('mapeamento_loja')
    .upsert(
      injectTenantId({
        instance_name: instanceName,
        loja_nome: lojaNome,
        updated_at: new Date().toISOString()
      }),
      { onConflict: 'instance_name' }
    );

  if (error) {
    console.error('Erro ao salvar mapeamento:', error);
    throw error;
  }

  return data;
};

/**
 * Buscar qual loja pertence uma instância
 */
export const buscarLojaPorInstancia = async (instanceName) => {
  const { data, error } = await withTenant(
    supabase
      .from('mapeamento_loja')
      .select('loja_nome')
      .eq('instance_name', instanceName)
  ).single();

  if (error && error.code !== 'PGRST116') { // PGRST116 = not found
    console.error('Erro ao buscar loja:', error);
    throw error;
  }

  return data?.loja_nome || null;
};

/**
 * Buscar configuração de IA de uma loja específica
 */
export const buscarConfigIAPorLoja = async (nomeLoja) => {
  const { data, error } = await withTenant(
    supabase
      .from('Configuracao_IA')
      .select('*')
      .eq('loja_nome', nomeLoja)
  ).single();

  if (error && error.code !== 'PGRST116') {
    console.error('Erro ao buscar config da loja:', error);
    throw error;
  }

  return data || null;
};

/**
 * Salvar/Atualizar configuração de IA de uma loja
 */
export const salvarConfigIA = async (nomeLoja, config) => {
  const { data, error } = await supabase
    .from('Configuracao_IA')
    .upsert(
      injectTenantId({
        loja_nome: nomeLoja,
        ...config,
        updated_at: new Date().toISOString()
      }),
      { onConflict: 'loja_nome' }
    );

  if (error) {
    console.error('Erro ao salvar config:', error);
    throw error;
  }

  return data;
};

/**
 * Excluir configuração de IA de uma loja
 */
export const excluirConfigIALoja = async (nomeLoja) => {
  const { error } = await withTenant(
    supabase
      .from('Configuracao_IA')
      .delete()
      .eq('loja_nome', nomeLoja)
  );

  if (error) {
    console.error('Erro ao excluir config:', error);
    throw error;
  }
};

// ============================================
// 📅 AGENDA (Banho & Tosa — Beleza Pet)
// MULTI-TENANT: todas as queries filtradas por tenant
// ============================================

/**
 * Busca sugestões de tutores/clientes para o autocomplete do formulário de agendamento.
 * Filtra pelo tenant ativo.
 */
export const buscarTutoresSugestao = async (termo) => {
  if (!termo || termo.length < 2) return [];
  try {
    const { data, error } = await withTenant(
      supabase
        .from('clientes')
        .select('nome, telefone')
        .or(`nome.ilike.%${termo}%,telefone.ilike.%${termo}%`)
        .limit(8)
    );
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('Erro ao buscar sugestões de tutor:', err);
    return [];
  }
};

/**
 * Lista todos os agendamentos do tenant ativo, ordenados por data e horário.
 */
export const listarAgenda = async () => {
  try {
    const { data, error } = await withTenant(
      supabase
        .from('agendamentos')
        .select('*')
        .order('data',    { ascending: true })
        .order('horario', { ascending: true })
    );
    if (error) throw error;
    return { sucesso: true, agendamentos: data || [] };
  } catch (error) {
    console.error('Erro ao listar agenda:', error);
    return { sucesso: false, agendamentos: [] };
  }
};

/**
 * Cria um novo agendamento para o tenant ativo.
 * Também garante que o tutor exista na tabela clientes.
 */
export const criarAgendamento = async (dados) => {
  // 1️⃣ Salva o agendamento com tenant_id
  const { data, error } = await supabase
    .from('agendamentos')
    .insert([injectTenantId({ ...dados, created_at: new Date().toISOString() })])
    .select()
    .single();

  if (error) throw error;

  // 2️⃣ Garante que o tutor existe em `clientes` (cria se não existir)
  try {
    const tenantId = getCurrentTenantId();
    const { data: existente } = await supabase
      .from('clientes')
      .select('id')
      .eq('telefone', dados.telefone)
      .eq('tenant_id', tenantId)
      .maybeSingle();

    if (!existente) {
      await supabase
        .from('clientes')
        .insert(injectTenantId({
          nome:                 dados.nome_tutor,
          telefone:             dados.telefone,
          active:               true,
          bloqueado_permanente: false,
        }));
    }
  } catch (clienteErr) {
    console.warn('[Agenda] Não foi possível sincronizar tutor em clientes:', clienteErr?.message);
  }

  return { sucesso: true, agendamento: data };
};

/**
 * Atualiza campos de um agendamento existente (inclusive status).
 */
export const atualizarAgendamento = async (id, dados) => {
  const { error } = await withTenant(
    supabase
      .from('agendamentos')
      .update({ ...dados, updated_at: new Date().toISOString() })
      .eq('id', id)
  );

  if (error) throw error;
  return { sucesso: true };
};

/**
 * Cancela um agendamento (atualiza status → 'cancelado').
 */
export const cancelarAgendamento = async (id) => {
  const { error } = await withTenant(
    supabase
      .from('agendamentos')
      .update({ status: 'cancelado', updated_at: new Date().toISOString() })
      .eq('id', id)
  );

  if (error) throw error;
  return { sucesso: true };
};

// ============================================
// 🤖 CONFIGURAÇÃO IA POR LOJA
// ============================================

/**
 * Buscar todas as configurações de IA (todas as lojas)
 */
export const buscarTodasConfigsIA = async () => {
  const { data, error } = await withTenant(
    supabase
      .from('Configuracao_IA')
      .select('*')
      .order('nome_empresa', { ascending: true })
  );

  if (error) {
    console.error('Erro ao buscar configs:', error);
    throw error;
  }

  return data || [];
};

// ============================================================
// PISCINAS
// ============================================================

/**
 * Carrega piscinas ativas de um cliente específico
 */
export const carregarPiscinasPorCliente = async (clienteId) => {
  try {
    const { data, error } = await withTenant(
      supabase
        .from('piscinas')
        .select('*')
        .eq('cliente_id', clienteId)
        .eq('ativo', true)
        .order('apelido', { ascending: true })
    );
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('Erro ao carregar piscinas:', err);
    throw err;
  }
};

/**
 * Salva (cria ou atualiza) uma piscina
 */
export const salvarPiscina = async (piscina, id = null) => {
  if (id) {
    const { data, error } = await withTenant(
      supabase.from('piscinas').update(piscina).eq('id', id)
    ).select().single();
    if (error) throw error;
    return data;
  }
  const { data, error } = await supabase
    .from('piscinas')
    .insert([injectTenantId(piscina)])
    .select()
    .single();
  if (error) throw error;
  return data;
};

/**
 * Exclui (soft delete) uma piscina pelo id
 */
export const excluirPiscina = async (id) => {
  const { error } = await withTenant(
    supabase.from('piscinas').delete().eq('id', id)
  );
  if (error) throw error;
};

// ============================================================
// ORDENS DE SERVIÇO
// ============================================================

/**
 * Carrega lista paginada de ordens de serviço com dados do cliente e piscina
 */
export const carregarOrdens = async ({ status = null, tipo = null, page = 0 } = {}) => {
  const limit = 50;
  const offset = page * limit;
  let query = withTenant(
    supabase
      .from('ordens_servico')
      .select('*,clientes(nome,telefone),piscinas(apelido,endereco)')
      .order('criado_em', { ascending: false })
      .range(offset, offset + limit - 1)
  );
  if (status) query = query.eq('status', status);
  if (tipo)   query = query.eq('tipo_servico', tipo);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

/**
 * Carrega uma OS pelo id, incluindo produtos
 */
export const carregarOSPorId = async (id) => {
  const { data, error } = await withTenant(
    supabase
      .from('ordens_servico')
      .select('*,clientes(nome,telefone),piscinas(apelido,endereco),os_produtos(*)')
      .eq('id', id)
  );
  if (error) throw error;
  return data?.[0] || null;
};

/**
 * Cria uma nova ordem de serviço com número sequencial por tenant via RPC
 */
export const criarOrdem = async (osData) => {
  const tenantId = getCurrentTenantId();
  // Busca próximo número de OS via RPC
  let numero = 1;
  try {
    const { data: numData, error: numErr } = await supabase.rpc('get_next_numero_os', {
      p_tenant_id: tenantId
    });
    if (!numErr && numData != null) numero = numData;
  } catch (e) {
    console.warn('get_next_numero_os falhou, usando 1:', e);
  }
  const { data, error } = await supabase
    .from('ordens_servico')
    .insert([injectTenantId({ ...osData, numero_os: numero })])
    .select()
    .single();
  if (error) throw error;
  return data;
};

/**
 * Atualiza campos de uma ordem de serviço existente
 */
export const atualizarOrdem = async (id, osData) => {
  const { data, error } = await withTenant(
    supabase.from('ordens_servico').update(osData).eq('id', id)
  ).select().single();
  if (error) throw error;
  return data;
};

/**
 * Exclui uma ordem de serviço pelo id
 */
export const excluirOrdem = async (id) => {
  const { error } = await withTenant(
    supabase.from('ordens_servico').delete().eq('id', id)
  );
  if (error) throw error;
};

/**
 * Substitui os produtos vinculados a uma OS (delete + insert)
 */
export const salvarOSProdutos = async (osId, produtos) => {
  // Remove produtos antigos
  const { error: delError } = await withTenant(
    supabase.from('os_produtos').delete().eq('os_id', osId)
  );
  if (delError) throw delError;
  // Insere novos produtos válidos
  const validos = (produtos || []).filter(p => p.nome_produto && p.nome_produto.trim());
  for (const item of validos) {
    const { error: insError } = await supabase
      .from('os_produtos')
      .insert([injectTenantId({ ...item, os_id: osId })]);
    if (insError) throw insError;
  }
};

// ============================================================
// AGENDA EVENTOS
// ============================================================
export const carregarEventosAgenda = async (ano, mes) => {
  // Load events for a given month (with 7-day buffer on each side for calendar edges)
  const inicio = new Date(ano, mes - 1, 1);
  inicio.setDate(inicio.getDate() - 7);
  const fim = new Date(ano, mes, 1);
  fim.setDate(fim.getDate() + 7);

  const { data, error } = await withTenant(
    supabase.from('agenda_eventos').select('*')
  )
    .gte('data_inicio', inicio.toISOString())
    .lte('data_inicio', fim.toISOString())
    .order('data_inicio', { ascending: true });

  if (error) throw error;
  return data || [];
};

export const criarEventoAgenda = async (evento) => {
  const { data, error } = await supabase
    .from('agenda_eventos')
    .insert([injectTenantId(evento)])
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const atualizarEventoAgenda = async (id, evento) => {
  const tenantId = getCurrentTenantId();
  const { data, error } = await supabase
    .from('agenda_eventos')
    .update(evento)
    .eq('id', id)
    .eq('tenant_id', tenantId)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const excluirEventoAgenda = async (id) => {
  const tenantId = getCurrentTenantId();
  const { error } = await supabase
    .from('agenda_eventos')
    .delete()
    .eq('id', id)
    .eq('tenant_id', tenantId);
  if (error) throw error;
};
